import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type ExchangeAdminItem,
  type ExchangeUpsertRequest,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";

// ---------------------------------------------------------------------------
// 交易所管理 (Exchanges)
//
// Lists every competitor exchange (+ Bitbase self) and lets the operator
// add new ones, edit aliases / domains / referral patterns, and remove
// unused ones. New exchanges become eligible for sponsorship_detection on
// the next 15-min cron tick — no restart needed (detector reloads
// exchanges per Pipeline.Run).
// ---------------------------------------------------------------------------

export default function Exchanges() {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ExchangeAdminItem | null>(null);

  const listQ = useQuery({
    queryKey: ["exchanges-admin"],
    queryFn: () => endpoints.exchangesAdmin(),
  });

  const rows = listQ.data ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">交易所</h1>
          <span className="text-sm text-muted-foreground">
            共 {rows.length} 家 · 推广检测词典在每次 detector 运行时自动重载
          </span>
          <RedetectButton className="ml-auto" />
          <Button size="sm" onClick={() => setShowCreate(true)}>
            + 新增交易所
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-background">
        {listQ.isLoading && <p className="p-4 text-sm text-muted-foreground">加载中…</p>}
        {listQ.isError && (
          <p className="p-4 text-sm text-destructive">
            加载失败：{(listQ.error as Error).message}
          </p>
        )}
        {rows.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>代码</TH>
                <TH>名称</TH>
                <TH>Tier</TH>
                <TH>身份</TH>
                <TH className="text-right">别名</TH>
                <TH className="text-right">域名</TH>
                <TH className="text-right">返佣码 pattern</TH>
                <TH className="text-right">关联推广</TH>
                <TH className="text-right">动作</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((e) => (
                <TR key={e.id}>
                  <TD className="font-mono text-xs">{e.code}</TD>
                  <TD className="font-medium">
                    <Link
                      to={`/exchanges/${e.id}`}
                      className="text-primary hover:underline"
                    >
                      {e.display_name}
                    </Link>
                  </TD>
                  <TD className="text-xs text-muted-foreground">{e.tier || "—"}</TD>
                  <TD>
                    {e.is_self ? (
                      <Badge variant="success">Bitbase 自身</Badge>
                    ) : (
                      <Badge variant="secondary">竞品</Badge>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums">{e.aliases.length}</TD>
                  <TD className="text-right tabular-nums">{e.domains.length}</TD>
                  <TD className="text-right tabular-nums">{e.referral_patterns.length}</TD>
                  <TD className="text-right tabular-nums">{e.sponsorship_count}</TD>
                  <TD className="text-right">
                    {e.is_self ? (
                      <span className="text-xs text-muted-foreground">
                        secrets 同步
                      </span>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(e)}
                        >
                          编辑
                        </Button>
                        <DeleteButton ex={e} />
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {showCreate && (
        <ExchangeModal
          mode="create"
          initial={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => setShowCreate(false)}
        />
      )}
      {editing && (
        <ExchangeModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// RedetectButton — operator triggers re-scan of historical videos with the
// current exchange/lexicon rules. Use case: just added a new competitor →
// want sponsorship_detection to look back through history with new rules.
// Exported as a stand-alone component so /system can also mount it.
export function RedetectButton({ className }: { className?: string }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (days: number) => endpoints.redetectSponsorships(days),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      const partial = data.videos_reset > (data.videos_scanned ?? 0)
        ? `\n剩余 ${data.videos_reset - (data.videos_scanned ?? 0)} 个会在下次 cron tick (15 分钟内) 处理`
        : "";
      const newCount = data.sponsorships_new ?? 0;
      const reconfirmed = data.sponsorships_reconfirmed ?? 0;
      const total = data.sponsorships_count ?? 0;
      const breakdown = total > 0
        ? `\n  ▸ 新发现 ${newCount} 条（多半来自新加的交易所/规则）` +
          `\n  ▸ 重新识别已有 ${reconfirmed} 条（规则仍命中）`
        : "";
      alert(
        `已重置 ${data.videos_reset} 个内容的检测状态\n` +
          `本次扫描 ${data.videos_scanned ?? 0} 个，命中 ${total} 条推广${breakdown}${partial}`,
      );
    },
    onError: (err) => alert((err as Error).message),
  });
  return (
    <Button
      className={className}
      size="sm"
      variant="outline"
      onClick={() => {
        const input = prompt(
          "重新检测：用当前所有交易所 + 词典规则重扫历史内容。\n窗口天数（默认 90，最大 730）：",
          "90",
        );
        if (input === null) return;
        const days = input.trim() === "" ? 90 : Number(input);
        if (Number.isNaN(days) || days < 1 || days > 730) {
          alert("请输入 1-730 的整数");
          return;
        }
        if (
          confirm(
            `将重置过去 ${days} 天所有内容的检测标记并立即重扫。已有的 confirmed/rejected sponsorship 不会丢，仅会补上漏检的。继续？`,
          )
        ) {
          mut.mutate(days);
        }
      }}
      disabled={mut.isPending}
    >
      {mut.isPending ? "重扫中…" : "重新检测历史"}
    </Button>
  );
}

function DeleteButton({ ex }: { ex: ExchangeAdminItem }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => endpoints.deleteExchange(ex.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exchanges-admin"] }),
    onError: (err) => alert((err as Error).message),
  });
  const disabled = ex.sponsorship_count > 0 || del.isPending;
  const title = ex.sponsorship_count > 0
    ? `有 ${ex.sponsorship_count} 条 sponsorship 引用，无法删除`
    : undefined;
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        if (confirm(`确认删除交易所 "${ex.display_name}" (${ex.code})？此操作不可撤销。`)) {
          del.mutate();
        }
      }}
      disabled={disabled}
      title={title}
    >
      {del.isPending ? "删除中…" : "删除"}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit Modal — single form, switch behavior via `mode` prop.
// aliases / domains / referral_patterns are entered as one-per-line in
// textareas (operator-friendly), parsed to string[] on save.
// ---------------------------------------------------------------------------

function ExchangeModal({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  initial: ExchangeAdminItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    code: initial?.code ?? "",
    display_name: initial?.display_name ?? "",
    tier: initial?.tier ?? "",
    aliases: (initial?.aliases ?? []).join("\n"),
    domains: (initial?.domains ?? []).join("\n"),
    referral_patterns: (initial?.referral_patterns ?? []).join("\n"),
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ExchangeUpsertRequest) => {
      if (mode === "create") return endpoints.createExchange(body);
      if (!initial) throw new Error("missing initial");
      return endpoints.updateExchange(initial.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exchanges-admin"] });
      qc.invalidateQueries({ queryKey: ["exchanges"] });
      onSaved();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.display_name.trim()) return setError("名称必填");
    if (mode === "create" && !form.code.trim()) return setError("代码必填");
    const body: ExchangeUpsertRequest = {
      display_name: form.display_name.trim(),
      tier: form.tier.trim() || undefined,
      aliases: splitLines(form.aliases),
      domains: splitLines(form.domains),
      referral_patterns: splitLines(form.referral_patterns),
    };
    if (mode === "create") body.code = form.code.trim().toUpperCase();
    mutation.mutate(body);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 className="mb-1 text-lg font-semibold">
          {mode === "create" ? "新增交易所" : `编辑交易所 ${initial?.code}`}
        </h2>
        <p className="mb-4 text-xs text-muted-foreground">
          保存后下一轮 sponsorship_detection（每 15 分钟）会用新规则扫描{" "}
          <strong>新内容</strong>。
          要让老内容也按新规则重扫，需要触发"重新检测"指令（暂未实装）或暂时手动 SQL
          重置 detection_done。
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">
              代码 *{mode === "edit" && "（不可修改）"}
            </span>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="BINANCE"
              disabled={mode === "edit"}
              required
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">显示名 *</span>
            <Input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="Binance"
              required
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Tier（可选）</span>
            <Input
              value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              placeholder="T1 / T2 / T3"
            />
          </label>
          <div />
          <label className="col-span-2 block">
            <span className="mb-1 block text-xs text-muted-foreground">
              别名（每行一个，detector 在内容标题 / 描述里匹配）
            </span>
            <textarea
              className="h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
              value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
              placeholder={"币安\nBinance\nBN"}
            />
          </label>
          <label className="col-span-2 block">
            <span className="mb-1 block text-xs text-muted-foreground">
              域名（每行一个，detector 在内容描述 URL 里匹配）
            </span>
            <textarea
              className="h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
              value={form.domains}
              onChange={(e) => setForm({ ...form, domains: e.target.value })}
              placeholder={"binance.com\nbinance.us"}
            />
          </label>
          <label className="col-span-2 block">
            <span className="mb-1 block text-xs text-muted-foreground">
              返佣码 pattern（每行一个，URL 子串，detector 用于识别 referral 链接）
            </span>
            <textarea
              className="h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
              value={form.referral_patterns}
              onChange={(e) =>
                setForm({ ...form, referral_patterns: e.target.value })
              }
              placeholder={"?ref=\n?referrer=\n/register?invite="}
            />
          </label>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function splitLines(raw: string): string[] {
  return raw
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s !== "");
}
